import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { useAuth } from "@/lib/auth";

const menuItems = [
  {
    label: "강스터디 소개",
    href: "/about",
    submenu: [
      { label: "브랜드 소개", href: "/about" },
      { label: "강사 소개", href: "/about/instructor" },
      { label: "수강 후기", href: "/reviews" },
    ],
  },
  {
    label: "경매 데이터",
    href: "/auctions",
    submenu: [
      { label: "전문가 PICK", href: "/auctions" },
      { label: "지역별 물건", href: "/auctions?tab=region" },
      { label: "낙찰인사이트", href: "/awarded-insights" },
      { label: "입찰계산기", href: "/bid-calculator" },
      { label: "AI 투자 시뮬레이터", href: "/investment-simulator" },
      { label: "물건 비교 계산기", href: "/compare" },
      { label: "관심물건", href: "/mypage/favorites" },
      { label: "물건분석", href: "/auctions/analysis" },
    ],
  },
  {
    label: "교육과정",
    href: "/courses",
    submenu: [
      { label: "무료강의", href: "/courses?type=free" },
      { label: "유료강의", href: "/courses?type=paid" },
      { label: "실전반", href: "/courses?type=practical" },
      { label: "오프라인 특강", href: "/courses?type=offline" },
      { label: "강의자료실", href: "/materials" },
    ],
  },
  {
    label: "동영상 강의",
    href: "/lectures",
    submenu: [
      { label: "입문", href: "/lectures?category=beginner" },
      { label: "권리분석", href: "/lectures?category=rights" },
      { label: "입찰가 산정", href: "/lectures?category=bidding" },
      { label: "명도", href: "/lectures?category=eviction" },
      { label: "매매사업자 과정", href: "/lectures?category=business" },
    ],
  },
  {
    label: "상담신청",
    href: "/consultation",
  },
  {
    label: "리포트",
    href: "/reports",
  },
  {
    label: "블로그",
    href: "/blog",
  },
  {
    label: "커뮤니티",
    href: "/community",
    submenu: [
      { label: "공지사항", href: "/community/notice" },
      { label: "질문게시판", href: "/community/questions" },
      { label: "후기게시판", href: "/community/reviews" },
    ],
  },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<number | null>(null);
  const location = useLocation();
  const { user, isAdmin, signOut } = useAuth();

  const isHomePage = location.pathname === "/";
  const shouldShowScrolled = isScrolled || !isHomePage;

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  const isParentActive = (item: typeof menuItems[number]) => {
    if (isActive(item.href)) return true;
    if (item.submenu) {
      return item.submenu.some((sub) => isActive(sub.href));
    }
    return false;
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveSubmenu(null);
  }, [location]);

  return (
    <motion.nav
      animate={{
        backgroundColor: shouldShowScrolled ? "rgba(255,255,255,1)" : "rgba(255,255,255,0)",
        borderBottomWidth: shouldShowScrolled ? 1 : 0,
        paddingTop: shouldShowScrolled ? 12 : 16,
        paddingBottom: shouldShowScrolled ? 12 : 16,
      }}
      transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] as const }}
      className="fixed top-0 left-0 right-0 z-50 border-[#E8E5E0]"
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between max-w-[1440px] mx-auto">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <motion.div
              animate={{
                backgroundColor: shouldShowScrolled ? "#C4956A" : "rgba(255,255,255,0.9)",
              }}
              transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] as const }}
              className="w-9 h-9 rounded-lg flex items-center justify-center"
            >
              <motion.i
                animate={{
                  color: shouldShowScrolled ? "#FFFFFF" : "#C4956A",
                }}
                transition={{ duration: 0.35 }}
                className="ri-auction-line text-lg"
              ></motion.i>
            </motion.div>
            <motion.span
              animate={{
                color: shouldShowScrolled ? "#1C1917" : "#FFFFFF",
              }}
              transition={{ duration: 0.35 }}
              className="text-xl font-bold"
            >
              강스터디
            </motion.span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-1">
            {menuItems.map((item, idx) => {
              const active = isParentActive(item);
              return (
                <div
                  key={idx}
                  className="relative"
                  onMouseEnter={() => setActiveSubmenu(idx)}
                  onMouseLeave={() => setActiveSubmenu(null)}
                >
                  <motion.div
                    animate={{
                      color: active
                        ? "#C4956A"
                        : shouldShowScrolled
                        ? "#5C5650"
                        : "rgba(255,255,255,0.9)",
                      backgroundColor: active
                        ? shouldShowScrolled
                          ? "#FAF8F6"
                          : "rgba(255,255,255,0.15)"
                        : "transparent",
                    }}
                    transition={{ duration: 0.3 }}
                    className="rounded-md"
                  >
                    <Link
                      to={item.href}
                      className="px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1 rounded-md hover:text-[#C4956A]"
                    >
                      {item.label}
                      {item.submenu && (
                        <i className="ri-arrow-down-s-line text-xs"></i>
                      )}
                    </Link>
                  </motion.div>

                  {/* Active indicator bar */}
                  {active && (
                    <motion.div
                      layoutId="navActiveIndicator"
                      className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#C4956A] rounded-full"
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}

                  {/* Invisible bridge to keep submenu open while mouse crosses the gap */}
                  <div className="absolute top-full left-0 w-full h-2" />

                  {/* Submenu Dropdown */}
                  {item.submenu && activeSubmenu === idx && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2, ease: "easeOut" as const }}
                      className="absolute top-[calc(100%+0.5rem)] left-0 bg-white rounded-lg border border-[#E8E5E0] py-2 min-w-[180px]"
                    >
                      {item.submenu.map((sub, subIdx) => (
                        <Link
                          key={subIdx}
                          to={sub.href}
                          className={`block px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                            isActive(sub.href)
                              ? "text-[#C4956A] bg-[#FAF8F6] font-medium"
                              : "text-[#5C5650] hover:text-[#C4956A] hover:bg-[#FAF8F6]"
                          }`}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </div>
              );
            })}

            {/* Admin Link */}
            <Link
              to="/admin"
              title="관리자"
              className="ml-1 w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
            >
              <motion.i
                animate={{
                  color: shouldShowScrolled ? "#8C8780" : "rgba(255,255,255,0.7)",
                }}
                transition={{ duration: 0.35 }}
                className="ri-dashboard-3-line text-base"
              ></motion.i>
            </Link>

            <motion.div
              animate={{
                backgroundColor: shouldShowScrolled ? "#1C1917" : "#FFFFFF",
                color: shouldShowScrolled ? "#FFFFFF" : "#1C1917",
              }}
              transition={{ duration: 0.35 }}
              className="ml-3 rounded-full"
            >
              {user ? (
                <Link
                  to="/mypage"
                  className="px-5 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap inline-block hover:bg-[#C4956A] hover:text-white"
                >
                  마이페이지
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="px-5 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap inline-block hover:bg-[#C4956A] hover:text-white"
                >
                  로그인
                </Link>
              )}
            </motion.div>
            {user && (
              <button
                type="button"
                onClick={() => {
                  void signOut();
                }}
                className={`ml-2 px-3 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${
                  shouldShowScrolled ? "text-[#8C8780] hover:text-[#1C1917]" : "text-white/70 hover:text-white"
                }`}
              >
                로그아웃
              </button>
            )}
          </div>

          {/* Mobile Hamburger */}
          <motion.button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            animate={{
              color: shouldShowScrolled ? "#1C1917" : "#FFFFFF",
            }}
            transition={{ duration: 0.35 }}
            whileTap={{ scale: 0.9 }}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10"
            aria-label="메뉴 열기"
          >
            <i
              className={`text-xl ${
                mobileMenuOpen ? "ri-close-line" : "ri-menu-line"
              }`}
            ></i>
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="md:hidden bg-white border-t border-[#E8E5E0] overflow-hidden"
          >
            <div className="px-4 py-3 space-y-1 max-h-[75vh] overflow-y-auto">
              {menuItems.map((item, idx) => {
                const active = isParentActive(item);
                return (
                  <div key={idx}>
                    {item.submenu ? (
                      <>
                        <button
                          onClick={() =>
                            setActiveSubmenu(activeSubmenu === idx ? null : idx)
                          }
                          className={`w-full flex items-center justify-between px-3 py-3 text-sm font-medium rounded-md transition-colors ${
                            active
                              ? "text-[#C4956A] bg-[#FAF8F6]"
                              : "text-[#1C1917] hover:bg-[#FAF8F6]"
                          }`}
                        >
                          {item.label}
                          <motion.i
                            animate={{ rotate: activeSubmenu === idx ? 180 : 0 }}
                            transition={{ duration: 0.25 }}
                            className="ri-arrow-down-s-line"
                          ></motion.i>
                        </button>
                        <AnimatePresence>
                          {activeSubmenu === idx && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.25, ease: "easeInOut" as const }}
                              className="ml-4 border-l-2 border-[#E8E5E0] pl-3 space-y-1 overflow-hidden"
                            >
                              {item.submenu.map((sub, subIdx) => (
                                <Link
                                  key={subIdx}
                                  to={sub.href}
                                  className={`block px-3 py-2 text-sm rounded-md transition-colors ${
                                    isActive(sub.href)
                                      ? "text-[#C4956A] font-medium"
                                      : "text-[#5C5650] hover:text-[#C4956A]"
                                  }`}
                                >
                                  {sub.label}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <Link
                        to={item.href}
                        className={`block px-3 py-3 text-sm font-medium rounded-md transition-colors ${
                          active
                            ? "text-[#C4956A] bg-[#FAF8F6]"
                            : "text-[#1C1917] hover:bg-[#FAF8F6]"
                        }`}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                );
              })}
              {user ? (
                <>
                  <Link
                    to="/mypage"
                    className="block mt-3 mx-3 px-5 py-3 text-sm font-medium text-center bg-[#1C1917] text-white rounded-full hover:bg-[#C4956A] transition-colors"
                  >
                    마이페이지
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      void signOut();
                    }}
                    className="block w-[calc(100%-1.5rem)] mx-3 mt-2 px-5 py-3 text-sm font-medium text-center border border-[#E8E5E0] text-[#5C5650] rounded-full hover:text-[#C4956A] hover:border-[#C4956A] transition-colors"
                  >
                    로그아웃
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="block mt-3 mx-3 px-5 py-3 text-sm font-medium text-center bg-[#1C1917] text-white rounded-full hover:bg-[#C4956A] transition-colors"
                >
                  로그인
                </Link>
              )}
              {/* 관리자 진입은 권한이 확인된 사용자에게만 보인다(판정 정본은 Edge Function). */}
              {isAdmin && (
                <Link
                  to="/admin"
                  className="block mx-3 mt-2 px-5 py-3 text-sm font-medium text-center border border-[#E8E5E0] text-[#5C5650] rounded-full hover:text-[#C4956A] hover:border-[#C4956A] transition-colors"
                >
                  <i className="ri-dashboard-3-line mr-1"></i>관리자
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}